/**
 * DV-STAGE-001: Stage Tester Page
 * Admin-only page for running pipeline stages in isolation with controllable inputs
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { ToggleLeft, ToggleRight, ClipboardCopy } from 'lucide-react';
import { cn } from '@/utils/cn';
import { getApiHeadersJson, buildApiUrl, API_ENDPOINTS } from '@/lib/api-base';
import { LogsPanel, type LogLevel } from './LogsPanel';
import { useStatusReport } from './useStatusReport';
import { StageCard, type StageId, type StageTesterState } from './StageCard';
import { useDemoLogger, type DemoLogEntry } from '@/lib/demo-logger';

const PIPELINE_STAGES: readonly { id: StageId; title: string; description: string }[] = [
  { id: 'script', title: 'Script', description: 'Generate video script from prompt' },
  { id: 'vision', title: 'Vision', description: 'Create visual concept and style guide' },
  { id: 'storyboard', title: 'Storyboard', description: 'Generate scene-by-scene storyboard' },
  { id: 'frames', title: 'Frames', description: 'Create individual frame images' },
  { id: 'video', title: 'Video', description: 'Generate video segments' },
  { id: 'qc', title: 'QC', description: 'Quality control evaluation' },
  { id: 'export', title: 'Export', description: 'Export final video assets' },
  { id: 'music', title: 'Music', description: 'Generate background music' },
] as const;

// Session storage key for persisting state
const SESSION_STORAGE_KEY = 'stage_tester_state';
const MAX_LOG_ENTRIES = 500; // Ring buffer size for logs

interface StageTesterPageProps {
  onLog?: (entry: DemoLogEntry) => void;
  className?: string;
  'data-testid'?: string;
}

// Session storage utilities
const getSessionState = (): Record<string, StageTesterState> => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to load stage tester state from session storage:', error);
    return {};
  }
};

const setSessionState = (state: Record<string, StageTesterState>): void => {
  try {
    // Store minimal state to avoid sessionStorage bloat
    const minimalState: Record<string, Partial<StageTesterState>> = {};
    Object.entries(state).forEach(([stageId, stageState]) => {
      minimalState[stageId] = {
        sandboxMode: stageState.sandboxMode,
        saveToExecution: stageState.saveToExecution,
        expanded: stageState.expanded,
      };
    });
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(minimalState));
  } catch (error) {
    console.warn('Failed to save stage tester state to session storage:', error);
  }
};

export function StageTesterPage({
  onLog: onGlobalLog,
  className,
  'data-testid': testId = 'stage-tester-page'
}: StageTesterPageProps) {
  const [chainMode] = useState(false);
  const [stageStates, setStageStates] = useState<Record<string, StageTesterState>>(() => {
    const sessionState = getSessionState();
    const initialState: Record<string, StageTesterState> = {};

    PIPELINE_STAGES.forEach(stage => {
      const defaults: StageTesterState = {
        promptPreview: null,
        promptHash: null,
        validationRequestId: null,
        sandboxMode: true,
        saveToExecution: false,
        isRunning: false,
        lastRun: null,
        expanded: false,
      };

      // Merge with session state
      initialState[stage.id] = {
        ...defaults,
        ...sessionState[stage.id],
      };
    });

    return initialState;
  });

  const [logs, setLogs] = useState<DemoLogEntry[]>([]);
  const [demoLogs, setDemoLogs] = useState<DemoLogEntry[]>([]);
  const [apiCalls, setApiCalls] = useState<DemoLogEntry[]>([]);
  const logsRef = useRef<DemoLogEntry[]>([]);
  const correlationCounter = useRef(0);

  const { buildStageTesterReport } = useStatusReport();

  // Initialize DemoLogger for this page
  const demoLogger = useDemoLogger('stage_tester', {
    component: 'StageTesterPage',
    onLog: (entry: DemoLogEntry) => {
      setDemoLogs(prev => {
        const updated = [...prev, entry];
        return updated.length > MAX_LOG_ENTRIES ? updated.slice(-MAX_LOG_ENTRIES) : updated;
      });

      // Track API calls separately for better status reporting
      if (entry.meta.action && ['fetch_start', 'fetch_success', 'fetch_error'].includes(entry.meta.action)) {
        setApiCalls(prev => {
          const updated = [...prev, entry];
          return updated.length > 50 ? updated.slice(-50) : updated; // Keep fewer API calls
        });
      }

      setLogs(prev => {
        const updated = [...prev, entry];
        return updated.length > MAX_LOG_ENTRIES ? updated.slice(-MAX_LOG_ENTRIES) : updated;
      });

      // Forward to global log handler
      if (onGlobalLog) {
        onGlobalLog(entry);
      }
    }
  });

  // Stabilize logger instance to prevent re-renders
  const demoLoggerRef = useRef(demoLogger);
  demoLoggerRef.current = demoLogger;

  // Initialize page logging - run only once on mount
  useEffect(() => {
    demoLoggerRef.current.info('Stage Tester page initialized', 'init', {
      total_stages: PIPELINE_STAGES.length,
      chain_mode: chainMode,
      session_state_loaded: Object.keys(getSessionState()).length > 0
    });
  }, []); // Empty dependency array - run only once on mount

  // Persist state changes to session storage
  useEffect(() => {
    setSessionState(stageStates);
  }, [stageStates]);

  // Generate correlation ID for tracking operations
  const generateCorrelationId = useCallback(() => {
    correlationCounter.current += 1;
    return `st-${Date.now()}-${correlationCounter.current.toString().padStart(3, '0')}`;
  }, []);

  // Legacy addLog for compatibility - now delegates to DemoLogger
  const addLog = useCallback((entry: Omit<DemoLogEntry, 'id'>) => {
    const level = entry.level as 'debug' | 'info' | 'warn' | 'error';
    demoLogger.log(level, entry.text, undefined, {
      ...entry.meta,
      correlationId: entry.meta?.correlationId || generateCorrelationId(),
    });
  }, [demoLogger, generateCorrelationId]);

  // Adapter for StageCard onLog interface
  const stageCardLogAdapter = useCallback((entry: { ts: string; level: LogLevel; text: string; meta?: any }) => {
    addLog({
      ts: entry.ts,
      level: entry.level,
      text: entry.text,
      meta: entry.meta || {}
    } as Omit<DemoLogEntry, 'id'>);
  }, [addLog]);

  // Copy Status Report functionality
  const handleCopyStatusReport = useCallback(async () => {
    try {
      demoLogger.info('Generating status report', 'user_action', {
        total_logs: demoLogs.length,
        total_api_calls: apiCalls.length,
        active_stages: Object.entries(stageStates).filter(([_, state]) => state.expanded || state.isRunning).length
      });

      const report = buildStageTesterReport(
        stageStates,
        chainMode,
        demoLogs,
        apiCalls
      );

      await navigator.clipboard.writeText(report);

      demoLogger.info('Status report copied to clipboard', 'user_action', {
        report_size: report.length,
        sections_included: ['configuration', 'active_stages', 'recent_operations', 'input_sources']
      });
    } catch (error) {
      demoLogger.error('Failed to copy status report', 'ui_error', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }, [buildStageTesterReport, stageStates, chainMode, demoLogs, apiCalls, demoLogger]);

  // Update state for specific stage
  const updateStageState = useCallback((stageId: StageId, updates: Partial<StageTesterState>) => {
    setStageStates(prev => ({
      ...prev,
      [stageId]: { ...prev[stageId], ...updates }
    }));

    // Log state changes for debugging with DemoLogger
    demoLogger.debug(`Stage ${stageId} state updated`, 'user_action', {
      stage: stageId,
      changes: Object.keys(updates),
      expanded: updates.expanded,
      sandboxMode: updates.sandboxMode,
      isRunning: updates.isRunning
    });
  }, [demoLogger]);

  // Handle stage run
  const handleStageRun = useCallback(async (stageId: StageId) => {
    const correlationId = generateCorrelationId();
    const url = buildApiUrl(API_ENDPOINTS.stageTester.run(stageId));

    demoLogger.info(`Starting stage run: ${stageId}`, 'user_action', {
      stage: stageId,
      correlationId,
      sandbox_mode: stageStates[stageId]?.sandboxMode
    });

    updateStageState(stageId, { isRunning: true });

    const startTime = Date.now();
    try {
      const stageState = stageStates[stageId];

      // Log API call start
      demoLogger.apiStart(url, 'POST', {
        stage: stageId,
        correlationId,
        sandbox_mode: stageState.sandboxMode
      });

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: getApiHeadersJson(),
        body: JSON.stringify({
          input: null, // TODO: Remove legacy run functionality
          overrides: {},
          sandboxMode: stageState.sandboxMode,
          save: false
        }),
      });

      const duration = Date.now() - startTime;

      if (!response.ok) {
        const errorText = await response.text();
        demoLogger.apiError(url, `${response.status} ${response.statusText}: ${errorText}`, duration, response.status, {
          stage: stageId,
          correlationId
        });
        throw new Error(`Stage execution failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      demoLogger.apiSuccess(url, duration, response.status, {
        stage: stageId,
        correlationId,
        result_size: JSON.stringify(result).length
      });

      updateStageState(stageId, {
        isRunning: false,
        lastRun: {
          timestamp: new Date().toISOString(),
          success: true,
          duration,
          cost: result.cost || 0,
          tokens: result.tokens || { input: 0, output: 0 },
          requestId: correlationId,
          output: result.output || result, // Store the actual output
        }
      });

      demoLogger.info(`Stage run completed: ${stageId}`, 'fetch_success', {
        stage: stageId,
        correlationId,
        duration,
        cost: result.cost || 0,
        tokens: result.tokens || { input: 0, output: 0 }
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      updateStageState(stageId, {
        isRunning: false,
        lastRun: {
          timestamp: new Date().toISOString(),
          success: false,
          duration: Date.now() - startTime,
          cost: 0,
          tokens: { input: 0, output: 0 },
          requestId: correlationId,
          error: errorMessage,
        }
      });

      demoLogger.error(`Stage run failed: ${stageId}`, 'fetch_error', {
        stage: stageId,
        correlationId,
        error: errorMessage
      });
    }
  }, [generateCorrelationId, demoLogger, updateStageState, stageStates]);

  // Handle stage cancel
  const handleStageCancel = useCallback(async (stageId: StageId) => {
    const correlationId = generateCorrelationId();

    demoLogger.warn(`Cancelling stage run: ${stageId}`, 'user_action', {
      stage: stageId,
      correlationId
    });

    try {
      // TODO: DV-STAGE-002 doesn't include cancel endpoint yet
      // For now, just update local state
      updateStageState(stageId, { isRunning: false });

      demoLogger.info(`Stage run cancelled successfully: ${stageId}`, 'user_action', {
        stage: stageId,
        correlationId
      });

    } catch (error) {
      updateStageState(stageId, { isRunning: false });

      demoLogger.error(`Failed to cancel stage run: ${stageId}`, 'ui_error', {
        stage: stageId,
        correlationId,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }, [generateCorrelationId, demoLogger, updateStageState]);

  // Clear all logs
  const clearLogs = useCallback(() => {
    setLogs([]);
    setDemoLogs([]);
    setApiCalls([]);
    logsRef.current = [];

    demoLogger.info('Logs cleared by user', 'clear');
  }, [demoLogger]);

  return (
    <div
      className={cn("flex flex-col h-full", className)}
      data-testid={testId}
    >
      {/* Header Bar */}
      <div
        className="flex items-center justify-between px-6 py-4 border-b"
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderColor: 'var(--border-default)',
        }}
      >
        <div>
          <h1
            className="text-xl font-bold"
            style={{
              color: 'var(--fg-primary)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            Stage Tester
          </h1>
          <p
            className="text-sm mt-1"
            style={{ color: 'var(--fg-muted)' }}
          >
            Run pipeline stages in isolation with controllable inputs
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Chain Mode Toggle (disabled for P1) */}
          <label className="flex items-center gap-2 opacity-50 cursor-not-allowed">
            <button
              disabled
              className="flex items-center"
              title="Chain mode will be available in DV-STAGE-010"
            >
              {chainMode ? (
                <ToggleRight className="w-5 h-5" style={{ color: 'var(--accent)' }} />
              ) : (
                <ToggleLeft className="w-5 h-5" style={{ color: 'var(--fg-muted)' }} />
              )}
            </button>
            <span
              className="text-sm font-mono"
              style={{
                color: 'var(--fg-muted)',
                fontFamily: 'var(--font-family-mono)',
              }}
            >
              Chain Mode
            </span>
          </label>

          {/* Copy Status Report Button */}
          <button
            onClick={handleCopyStatusReport}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors focus:outline-none focus:ring-2 hover:bg-opacity-80"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-default)',
              color: 'var(--fg-primary)',
            }}
            data-testid={`${testId}-copy-status`}
            title="Copy comprehensive status report with logs, stage states, and debug info"
          >
            <ClipboardCopy className="w-4 h-4" />
            <span
              className="text-sm font-mono"
              style={{ fontFamily: 'var(--font-family-mono)' }}
            >
              Copy Status Report
            </span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Stage List */}
        <div
          className="w-2/3 overflow-y-auto border-r"
          style={{ borderColor: 'var(--border-default)' }}
        >
          <div className="p-6 space-y-4">
            {PIPELINE_STAGES.map((stage) => (
              <StageCard
                key={stage.id}
                stageId={stage.id}
                title={stage.title}
                description={stage.description}
                state={stageStates[stage.id]}
                onChange={(updates) => updateStageState(stage.id, updates)}
                onRun={() => handleStageRun(stage.id)}
                onCancel={() => handleStageCancel(stage.id)}
                onLog={stageCardLogAdapter}
                data-testid={`${testId}-stage-${stage.id}`}
              />
            ))}
          </div>
        </div>

        {/* Logs Panel */}
        <div className="w-1/3 flex flex-col">
          <LogsPanel
            entries={logs}
            onClear={clearLogs}
            className="flex-1"
            minHeight={300}
            maxHeight={600}
            enableRedaction={true}
            defaultRedacted={false}
            adminRawAccess={true}
            data-testid={`${testId}-logs`}
          />
        </div>
      </div>
    </div>
  );
}